import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

import { SpotlightTour } from "@/components/product-tour/SpotlightTour";
import { ProductTourContext } from "@/components/product-tour/useProductTour";
import { WelcomeTourDialog } from "@/components/product-tour/WelcomeTourDialog";
import {
  collectPresentTourAnchors,
  resolveAppHomeTourSteps,
  TOUR_ANCHOR,
  waitForTourAnchor,
  type ResolvedTourStep,
} from "@/components/product-tour/tourSteps";
import {
  isWalkthroughDone,
  setWalkthroughDone,
} from "@/components/product-tour/walkthroughStorage";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { hasNavigableAppAccess } from "@/lib/permissions";

type TourPhase = "idle" | "welcome" | "spotlight";

const HOME_PATHS = new Set(["/dashboard", "/home"]);
const ANCHOR_WAIT_MS = 2000;

type ProductTourProviderProps = {
  children: ReactNode;
};

function isHomePath(pathname: string): boolean {
  return HOME_PATHS.has(pathname);
}

export function ProductTourProvider({ children }: ProductTourProviderProps): ReactElement {
  const { user } = useAuth();
  const { can } = usePermissions();
  const location = useLocation();
  const [phase, setPhase] = useState<TourPhase>("idle");
  const [steps, setSteps] = useState<ResolvedTourStep[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const skipWelcomeCloseRef = useRef(true);
  const startGenerationRef = useRef(0);

  const userId = user?.id ?? null;
  const canShowTour = hasNavigableAppAccess(user);

  const completeTour = useCallback((): void => {
    startGenerationRef.current += 1;
    if (userId) setWalkthroughDone(userId, "appHome", true);
    skipWelcomeCloseRef.current = true;
    setPhase("idle");
    setSteps([]);
    setStepIndex(0);
  }, [userId]);

  const openWelcome = useCallback((): void => {
    skipWelcomeCloseRef.current = true;
    setSteps([]);
    setStepIndex(0);
    setPhase("welcome");
  }, []);

  useEffect(() => {
    if (!userId) return;
    if (!canShowTour) return;
    if (phase !== "idle") return;
    if (isWalkthroughDone(userId, "appHome")) return;
    if (!isHomePath(location.pathname)) return;

    let cancelled = false;
    const started = Date.now();
    const tick = (): void => {
      if (cancelled) return;
      const ready = collectPresentTourAnchors().has(TOUR_ANCHOR.appNav);
      if (ready || Date.now() - started >= ANCHOR_WAIT_MS) {
        openWelcome();
        return;
      }
      requestAnimationFrame(tick);
    };
    const frame = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [canShowTour, location.pathname, openWelcome, phase, userId]);

  const startSpotlight = useCallback(async (): Promise<void> => {
    const generation = startGenerationRef.current + 1;
    startGenerationRef.current = generation;
    const preferred = can("nav.dashboard") ? TOUR_ANCHOR.dashboardKpis : TOUR_ANCHOR.memberProjects;
    await waitForTourAnchor(preferred, ANCHOR_WAIT_MS);
    if (generation !== startGenerationRef.current) return;
    const nextSteps = resolveAppHomeTourSteps({
      can,
      presentAnchors: collectPresentTourAnchors(),
    });
    if (nextSteps.length === 0) {
      completeTour();
      return;
    }
    skipWelcomeCloseRef.current = false;
    setSteps(nextSteps);
    setStepIndex(0);
    setPhase("spotlight");
  }, [can, completeTour]);

  const skipWelcome = useCallback((): void => {
    if (!skipWelcomeCloseRef.current) return;
    completeTour();
  }, [completeTour]);

  const replayAppHomeTour = useCallback((): void => {
    if (!userId || !canShowTour) return;
    setWalkthroughDone(userId, "appHome", false);
    openWelcome();
  }, [canShowTour, openWelcome, userId]);

  const contextValue = useMemo(() => ({ replayAppHomeTour }), [replayAppHomeTour]);

  return (
    <ProductTourContext.Provider value={contextValue}>
      {children}
      <WelcomeTourDialog
        open={phase === "welcome"}
        onStart={() => {
          void startSpotlight();
        }}
        onSkip={skipWelcome}
      />
      {phase === "spotlight" && steps.length > 0 ? (
        <SpotlightTour
          steps={steps}
          stepIndex={stepIndex}
          onBack={() => setStepIndex((index) => Math.max(0, index - 1))}
          onNext={() => setStepIndex((index) => Math.min(steps.length - 1, index + 1))}
          onDone={completeTour}
        />
      ) : null}
    </ProductTourContext.Provider>
  );
}
